/**
 * Allys Company Story Component
 * 
 * Premium styled content section with Allys design language
 */

'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import type { CompanyStoryProps } from '@routeroyal/theme-core';

/**
 * Allys Company Story - Clean, modern design with smooth animations
 */
export function AllysCompanyStory({
  title,
  content,
  image,
  imagePosition = 'right',
  stats,
  background = 'white'
}: CompanyStoryProps) {
  const contentArray = Array.isArray(content) ? content : [content];
  const bgClass = background === 'gray' ? 'bg-gray-50' : 'bg-white';
  
  return (
    <section className={`py-24 lg:py-32 ${bgClass} relative overflow-hidden`}>
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.015] bg-[radial-gradient(circle_at_50%_50%,var(--color-primary)_1px,transparent_1px)] bg-[length:40px_40px]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`grid grid-cols-1 ${image ? 'lg:grid-cols-2' : ''} gap-12 lg:gap-20 items-center`}>
          {/* Image - Left */}
          {image && imagePosition === 'left' && (
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl"
            >
              <Image
                src={image}
                alt={title}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </motion.div>
          )}
          
          {/* Content */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-[#040813] mb-8 tracking-tight [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
            >
              {title}
            </motion.h2>
            
            <div className="space-y-6">
              {contentArray.map((paragraph, index) => (
                <motion.p
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.8, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="text-lg lg:text-xl text-gray-600 leading-relaxed font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>
            
            {/* Stats */}
            {stats && stats.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="grid grid-cols-2 md:grid-cols-3 gap-8 lg:gap-10 mt-14"
              >
                {stats.map((stat, index) => (
                  <motion.div 
                    key={index} 
                    className="group relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.6, delay: 0.5 + index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    {/* Subtle background card */}
                    <div className="absolute inset-0 bg-white/40 backdrop-blur-sm rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Content */}
                    <div className="relative p-6 rounded-xl border border-transparent group-hover:border-[#BBA339]/20 transition-all duration-300">
                      {/* Gold accent line */}
                      <div className="h-1 w-12 bg-gradient-to-r from-[#BBA339] to-[#BBA339]/50 rounded-full mb-4" />
                      
                      <div className="text-4xl lg:text-5xl font-heading font-bold text-[#BBA339] mb-3 tracking-tight [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] drop-shadow-sm">
                        {stat.value}
                      </div>
                      <div className="text-sm lg:text-base text-[#040813] font-medium [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] uppercase tracking-wide">
                        {stat.label}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
          
          {/* Image - Right */}
          {image && imagePosition === 'right' && (
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl"
            >
              <Image
                src={image}
                alt={title}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

